import { useEffect, useState } from "react";
import { Plus, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PIPELINE_COLUMNS, TYPE_LABEL, columnOfStatus } from "@/lib/constants";
import { FileUpload, resolveMediaUrl, mergeUploadsIntoForm } from "@/components/FileUpload";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const cls = "w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400";

// Central "create new content" dialog: title/type/status + media upload + cloud link.
export function ContentCreateDialog({ accountKey, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initial());
  const [members, setMembers] = useState([]);
  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (!open || members.length > 0) return;
    api.get("/chat/members")
      .then(({ data }) => setMembers(Array.isArray(data) ? data : []))
      .catch(() => setMembers([]));
  }, [open, members.length]);

  function initial() {
    return {
      title: "", content_type: "reel", status: "idea", hook: "", caption: "",
      cta: "", assigned_creator: "", scheduled_date: "", source_link: "",
      media_url: "", video_url: "", carousel_urls: [],
    };
  }

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Titel erforderlich");
    setSaving(true);
    try {
      await api.post("/content", { account_key: accountKey, ...form, scheduled_date: form.scheduled_date || null });
      if (form.status === "review") window.dispatchEvent(new Event("approvals:changed"));
      toast.success("Inhalt angelegt");
      setOpen(false);
      setForm(initial());
      onCreated?.();
    } catch {
      toast.error("Anlegen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-content-btn" className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800 transition">
          <Plus className="h-4 w-4" /> Neuer Inhalt
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head">Neuen Inhalt anlegen</DialogTitle>
          <DialogDescription>Lade dein fertiges Video/Bild hoch oder verlinke es aus der Cloud (z. B. OneDrive).</DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5">
          {/* Media */}
          <div className="rounded-lg border border-neutral-200 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Fertiges Material</span>
              <FileUpload accept="video/*,image/*" label="Video / Bild hochladen" multiple
                onUploaded={(d) => { if (d.kind === "video") f("video_url", d.url); }}
                onBatchUploaded={(items) => setForm((s) => mergeUploadsIntoForm(s, items))} />
            </div>
            {(form.carousel_urls || []).length > 0 && (
              <div data-testid="create-carousel-info" className="mt-2 flex items-center justify-between rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-[13px] text-violet-800">
                <span>🖼️ <b>{form.carousel_urls.length} Medien</b> im Karussell</span>
                <button
                  type="button"
                  data-testid="create-carousel-clear"
                  onClick={() => setForm((s) => ({ ...s, carousel_urls: [], media_url: "", content_type: s.content_type === "carousel" ? "post" : s.content_type }))}
                  className="text-[12px] font-semibold text-violet-600 hover:underline shrink-0 ml-2"
                >
                  Leeren
                </button>
              </div>
            )}
            {(form.video_url || form.media_url) && (
              <div className="mt-2 rounded-md overflow-hidden border border-neutral-200">
                {form.video_url
                  ? <video src={resolveMediaUrl(form.video_url)} controls className="w-full max-h-48" />
                  : <img src={resolveMediaUrl(form.media_url)} alt="" className="w-full max-h-48 object-cover" />}
              </div>
            )}
          </div>

          {/* Cloud link */}
          <Field label="Cloud-/Content-Link (optional, z. B. OneDrive)">
            <div className="flex gap-2">
              <input data-testid="create-source-link" value={form.source_link} onChange={(e) => f("source_link", e.target.value)} placeholder="https://onedrive.live.com/…" className={cls} />
              {form.source_link && (
                <a href={form.source_link} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center px-3 rounded-lg border border-neutral-200 hover:bg-neutral-50">
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </Field>

          <Field label="Titel">
            <input data-testid="create-title" value={form.title} onChange={(e) => f("title", e.target.value)} className={cls} placeholder="z. B. Wochenmarkt-Reel" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Format">
              <select value={form.content_type} onChange={(e) => f("content_type", e.target.value)} className={cls}>
                {["reel", "story", "post", "carousel"].map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select data-testid="create-status" value={columnOfStatus(form.status).key} onChange={(e) => f("status", PIPELINE_COLUMNS.find((c) => c.key === e.target.value).drop)} className={cls}>
                {PIPELINE_COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Hook">
            <input value={form.hook} onChange={(e) => f("hook", e.target.value)} className={cls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Creator">
              <select
                data-testid="create-creator-select"
                value={form.assigned_creator}
                onChange={(e) => f("assigned_creator", e.target.value)}
                className={`${cls} bg-white`}
              >
                <option value="">— Niemand —</option>
                {members.map((member) => (
                  <option key={member.id} value={member.name}>
                    {member.name}{member.role === "admin" ? " (Admin)" : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Geplant am">
              <input type="datetime-local" data-testid="create-scheduled-date" value={form.scheduled_date} onChange={(e) => f("scheduled_date", e.target.value)} className={cls} />
            </Field>
          </div>
        </div>

        <DialogFooter>
          <button data-testid="create-save-btn" onClick={submit} disabled={saving} className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-neutral-800 disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Inhalt speichern
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-neutral-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
